import { Component, Input, OnDestroy, OnInit, inject, signal } from '@angular/core';
import { Order } from '../../interfaces/payment-summary.interface';
import { PaymentsService } from '../../data-access/payments.service';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { ShoppingCartComponent } from '../../ui/shopping-cart/shopping-cart.component';
import { Subject, catchError, delay, map, mergeMap, of, switchMap, takeUntil, tap } from 'rxjs';
import { CartStateService } from '../../data-access/cart-state.service';
import { CouponCodeComponent } from '../../ui/coupon-code/coupon-code.component';
import { DonationComponent } from '../../ui/donation/donation.component';
import { PaymentTypeComponent } from '../../ui/payment-type/payment-type.component';
import { CartTotalsComponent } from '../../ui/cart-totals/cart-totals.component';
import { MatSnackBar } from '@angular/material/snack-bar';
import { EmptyCartComponent } from '../../ui/empty-cart/empty-cart.component';
import { BraintreeService } from '../../data-access/braintree.service';


@Component({
  standalone: true,
  imports: [
    CommonModule,

    ShoppingCartComponent,
    CouponCodeComponent,
    DonationComponent,
    PaymentTypeComponent,
    CartTotalsComponent,
    EmptyCartComponent
  ],
  providers: [
    PaymentsService
  ],
  selector: 'equine-ui-nx-payment-summary',
  templateUrl: './payment-summary.component.html',
  styleUrls: ['./payment-summary.component.scss']
})
export class PaymentSummaryComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private paymentService = inject(PaymentsService);
  private cartState = inject(CartStateService);
  private snackBar = inject(MatSnackBar);
  
  private braintreeService = inject(BraintreeService);
  hasBraintreeVerifications = signal(false);

  cartReference = this.paymentService.getLoggedInUserId() || '';

  orders = this.cartState.ordersForSum;
  subTotal = this.cartState.subtotal;
  totalItems = this.cartState.totalItems;
  donationOrder = this.cartState.donationOrder;
  donationOrderTotal = this.cartState.donationOrderTotal;
  total = this.cartState.total;
  coupon = this.cartState.coupon;
  couponAndDonationSectionsEnabled = this.cartState.couponAndDonationSectionsEnabled;

  emptyError = this.cartState.emptyCart;
  initialLoading = signal(true);

  paymentType: string | null = null;
  isAdmin = false;
  private addOrRemoveDonation$ = new Subject<{action: 'remove' | 'add', value: any}>();
  private unsubscribe$ = new Subject<void>();

  ngOnInit(): void {
    this.isAdmin = this.paymentService.isAdminRole();
    this.paymentService.getCartSummary(undefined, this.cartReference)
      .subscribe({
        next: () => {
          this.initialLoading.set(false);
          if (!this.orders().length) {
            this.checkPendingBraintreeVerifications();
          }
        },
        error: () => {
          this.initialLoading.set(false);
        }
      });

    this.subscribeToDonation();
  }

  removeOrderFromCart(orderId: string) {
    this.paymentService.removeFromCart(this.cartReference, [orderId])
      .pipe(
        switchMap(() => this.paymentService.getCartSummary(undefined, this.cartReference))
      )
      .subscribe({
        error: (error: any) => {
          this.snackBar.open(error.error.message, 'Dismiss');
        }
      })
  }

  addDonation(code: "D-05" | "D-10" | "D-25") {
    this.addOrRemoveDonation$.next({
      action: 'add',
      value: code
    });
  }

  removeDonation() {
    this.addOrRemoveDonation$.next({
      action: 'remove',
      value: null
    });
  }

  subscribeToDonation() {
    this.addOrRemoveDonation$
      .pipe(
        takeUntil(this.unsubscribe$),
        switchMap(({action, value}) => {
          if (action === 'add') {
            return this.getAddDonationStream(value)
          } else {
            return this.getRemoveDonationStream()
          }
        }),
        catchError(() => {
          return of();
        }),
        delay(50),
        switchMap(() => this.paymentService.getCartSummary(undefined, this.cartReference))
      )
      .subscribe();
  }

  getAddDonationStream(code: "D-05" | "D-10" | "D-25") {
    return this.paymentService.getCurrentDonation(undefined, this.cartReference)
      .pipe(
        switchMap((response) => this.paymentService.createDonationOrder(this.cartReference, response.additionalReference, code)),
        switchMap((donation) => this.paymentService.addToCart(this.cartReference, [donation.id], this.cartReference))
      )
  }

  getRemoveDonationStream() {
    if (this.donationOrder()) {
      return this.paymentService.removeFromCart(this.cartReference, [(this.donationOrder() as Order).id]);
    } else {
      return of();
    }
  }

  payWithPaypal() {
    this.router.navigate(['payments', 'pay-with-paypal']);
  }
  
  payWithCard() {
    this.router.navigate(['payments', 'pay-with-card']);
  }

  payWithACH() {
    this.router.navigate(['payments', 'pay-with-bt-ach']);
  }

  onCouponApplied(coupon: string) {

    this.paymentService.addCouponToCart(this.cartReference, coupon)
      .pipe(
        takeUntil(this.unsubscribe$),
        tap((response: any) => this.snackBar.open(`SUCCESS: ${response.name} added to cart`, 'Dismiss')),
        switchMap(() => this.paymentService.getCartSummary(undefined, this.cartReference))
      )
      .subscribe({
        error: (error) => {
          if (error.error?.message) {
            this.snackBar.open('ERROR: ' + error.error.message, 'Dismiss');
          } else {
            this.snackBar.open('ERROR: Coupon not found', 'Dismiss');
          }
        }
      })
  }

  payWithCoupon() {
    this.paymentService.applyCouponToFullfillPayment(this.cartReference)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response) => {
          this.paymentService.setCartAsEmptyAfterPayment();
          this.snackBar.open(`Success!`, 'Dismiss', {
            duration: 5000,
            panelClass: ['success-snackbar'],
          });
          setTimeout(() => {
            this.router.navigate(['payments', 'success'])
          }, 10);
        },
        error: () => {
          this.snackBar.open(`Error: an error occured processing coupon`, 'Dismiss');
        }
      })
  }

  setPaymentType(type: string) {
    this.paymentType = type;
  }

  proceedToCheckout() {
    if (this.total() === 0) {
      this.payWithCoupon();
      return;
    }
    if (this.paymentType === 'paypal') {
      this.payWithPaypal();
    }
    if (this.paymentType === 'card') {
      this.payWithCard();
    }
    if (this.paymentType === 'ach') {
      this.payWithACH();
    }
  }

  goToDashboard() {
    this.router.navigate(['dashboard']);
  }

  checkPendingBraintreeVerifications() {
    this.braintreeService.checkPendingBraintreeVerifications(this.cartReference)  
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe({
        next: (response: {pending: boolean}) => {
          this.hasBraintreeVerifications.set(response.pending);
        }
      })
  }

  goToAccountVerificationView() {
    this.router.navigate(['payments', 'bank-account-verification'])
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }

}
